import { hex, sha256, utf8, unhex } from "./encoding";
import type { WebAuthnAuthentication as PasskeyAuthentication } from "./storage";
const b64 = (v: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(v)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
const bytes = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/")), (c) =>
    c.charCodeAt(0),
  );
export async function passkeySalt(scope: string): Promise<string> {
  return hex(
    await sha256(utf8("StealthOS Stellar private wallet PRF v1:" + scope)),
  );
}
function validateResponse(
  c: PublicKeyCredential,
  challenge: Uint8Array,
  type: string,
) {
  const data = JSON.parse(new TextDecoder().decode(c.response.clientDataJSON));
  if (
    data.type !== type ||
    data.origin !== location.origin ||
    data.crossOrigin ||
    data.challenge !== b64(challenge.buffer as ArrayBuffer)
  )
    throw Error("Passkey origin or challenge mismatch.");
  const response = c.response as AuthenticatorAssertionResponse &
    AuthenticatorAttestationResponse;
  const auth = new Uint8Array(
    type === "webauthn.get"
      ? response.authenticatorData
      : response.getAuthenticatorData(),
  );
  if (auth.length < 37 || (auth[32] & 5) !== 5)
    throw Error("Device user verification is required.");
}
export async function privateWalletPasskey(
  scope: string,
  existing?: PasskeyAuthentication,
) {
  if (location.hostname === "127.0.0.1")
    throw Error(
      "Device authentication requires http://localhost:5180/stellar. Existing encrypted wallets remain available at the original address for recovery.",
    );
  if (!globalThis.PublicKeyCredential || !navigator.credentials)
    throw Error("This browser cannot securely unlock a passkey wallet.");
  const salt = await passkeySalt(scope);
  if (
    existing &&
    (existing.origin !== location.origin ||
      existing.prfSalt !== salt ||
      existing.type !== "webauthn-prf")
  )
    throw Error("Passkey belongs to another wallet or origin.");
  const evalInput = { first: unhex(salt) as BufferSource };
  let credential: PublicKeyCredential;
  let metadata = existing;
  if (!metadata) {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    credential = (await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: "StealthOS private payments" },
        user: {
          id: (await sha256(utf8(scope))) as BufferSource,
          name: "Stellar Testnet wallet",
          displayName: "StealthOS private wallet",
        },
        pubKeyCredParams: [{ type: "public-key", alg: -7 }],
        authenticatorSelection: {
          residentKey: "required",
          userVerification: "required",
        },
        attestation: "none",
        timeout: 60000,
        extensions: { prf: { eval: evalInput } } as any,
      },
    })) as PublicKeyCredential;
    if (!credential) throw Error("Passkey creation was cancelled.");
    validateResponse(credential, challenge, "webauthn.create");
    metadata = {
      type: "webauthn-prf",
      credentialId: b64(credential.rawId),
      prfSalt: salt,
      origin: location.origin,
    };
  } else {
    credential = null as any;
  }
  let output = (credential?.getClientExtensionResults() as any)?.prf?.results
    ?.first;
  if (!output) {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    credential = (await navigator.credentials.get({
      publicKey: {
        challenge,
        timeout: 60000,
        allowCredentials: [
          {
            type: "public-key",
            id: bytes(metadata.credentialId) as BufferSource,
          },
        ],
        userVerification: "required",
        extensions: { prf: { eval: evalInput } } as any,
      },
    })) as PublicKeyCredential;
    if (!credential) throw Error("Passkey unlock was cancelled.");
    validateResponse(credential, challenge, "webauthn.get");
    if (b64(credential.rawId) !== metadata.credentialId)
      throw Error("A different passkey was returned.");
    output = (credential.getClientExtensionResults() as any)?.prf?.results
      ?.first;
  }
  if (!(output instanceof ArrayBuffer) || output.byteLength !== 32)
    throw Error(
      "This authenticator does not support secure PRF encryption. No wallet was created or overwritten.",
    );
  const secret = new Uint8Array(output);
  const password = hex(secret);
  secret.fill(0);
  return { password, authentication: metadata };
}
